import { useNotificationStore } from '../store/notificationStore';

/**
 * The single fetch that must accompany an OPEN transition of the notification panel
 * (M6 · 86d44z2b7, must-fix 2).
 *
 * ⛔ Fire-and-forget, deliberately: the panel must paint its skeleton BEFORE this resolves. An
 * `await` between the click and the first visual change is the exact violation the responsiveness
 * gate names.
 *
 * ⛔ ONE REQUEST, AND IT IS THIS ONE. The panel never calls `fetchMore` and ignores `nextCursor`.
 *
 * ⚠️ NOT a mount effect. A mount effect fires on every remount — and the panel is conditionally
 * rendered, so it remounts on each open AND on any parent re-render that toggles it, turning "one
 * request per open" into "a request whenever React feels like it". It belongs on the transition,
 * which is why this is a plain function called from the bell's click handler.
 *
 * ⚠️ THE STORE IS SHARED WITH THE /notifications PAGE, and this panel's contract is the ALL slice —
 * so when the page has left the store on another tab, opening goes through `setTab('all')` (the
 * documented reset: bumps reqSeq, discards the other tab's in-flight pages) rather than
 * force-fetching `tab:'all'` into a window whose `tab` still says otherwise. The 2026-08-25 sweep
 * caught that desync: all-tab rows rendered under a "Needs action" header, and every later SSE
 * upsert gated by the WRONG tab predicate.
 *
 * Cost, accepted: opening the panel resets the page's tab selection to All. The page's needs-first
 * default re-asserts itself on the next genuinely empty visit.
 */
export function primeNotificationsOnOpen(): void {
  const store = useNotificationStore.getState();
  if (store.tab !== 'all') {
    store.setTab('all'); // resets + refetches (includePinned rides on setTab's own fetch)
  } else {
    void store.fetch({ includePinned: true, includeCounts: true });
  }
}

/**
 * The one accessible name for the bell — its badge is `aria-hidden`, so this is the only route the
 * counts have to assistive tech.
 */
export function bellLabel(needsCount: number, unreadCount: number): string {
  if (needsCount === 0 && unreadCount === 0) return 'Notifications — nothing new';

  const parts: string[] = [];
  if (needsCount > 0) parts.push(`${needsCount} need attention`);
  if (unreadCount > 0) parts.push(`${unreadCount} unread`);
  return `Notifications — ${parts.join(', ')}`;
}
